import { reactive, readonly, watch } from 'vue'

/**
 * Глобальный тогл для декоративного фона админ-панели (`mrsimkin.jpg`).
 *
 * Фон рисуется в `assets/main.css` через `body::before`. Этот composable
 * управляет атрибутом `data-bg` на `<html>`:
 *
 *   <html data-bg="on">  → body::before display: block (фон виден);
 *   <html data-bg="off"> → body::before display: none.
 *
 * Состояние персистится в `localStorage('admin.bg')`. Anti-flash
 * выставление атрибута лежит в `index.html` — чтобы фон не «моргал» при
 * первом рендере.
 *
 * Делаю отдельный composable (а не часть useTheme), потому что:
 *   * тема и наличие фоновой картинки — независимые предпочтения;
 *   * в логику темы (auto / system) добавление «есть картинка» путает.
 */

export type BackgroundMode = 'on' | 'off'

const STORAGE_KEY = 'admin.bg'

function readStored(): BackgroundMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (v === 'on' || v === 'off') return v
  } catch {
    /* private mode → дефолт */
  }
  return 'on'
}

function apply(mode: BackgroundMode): void {
  document.documentElement.setAttribute('data-bg', mode)
}

const state = reactive<{ mode: BackgroundMode }>({ mode: readStored() })
apply(state.mode)

watch(
  () => state.mode,
  (mode) => {
    try {
      localStorage.setItem(STORAGE_KEY, mode)
    } catch {
      /* noop */
    }
    apply(mode)
  },
)

function setMode(mode: BackgroundMode): void {
  state.mode = mode
}

function toggle(): void {
  setMode(state.mode === 'on' ? 'off' : 'on')
}

export function useBackground() {
  return {
    state: readonly(state),
    setMode,
    toggle,
  }
}
